/**
 * Platform Review Abstraction
 * 
 * Handles app store review requests differently on web vs native.
 * On native, uses the in-app review API (App Store / Play Store).
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';

export interface ReviewPlatform {
  /** Request an in-app review prompt */
  requestReview(): Promise<void>;
  
  /** Open the app store page for the app */
  openStore(): Promise<void>;
  
  /** Check if review functionality is available */
  isAvailable(): boolean;
}

/**
 * Web implementation - review not available on web
 */
class WebReview implements ReviewPlatform {
  async requestReview(): Promise<void> {
    // No-op on web
  }

  async openStore(): Promise<void> {
    // No-op on web
  }

  isAvailable(): boolean {
    return false;
  }
}

/**
 * Native implementation - uses native in-app review APIs
 */
class NativeReview implements ReviewPlatform {
  async requestReview(): Promise<void> {
    try {
      await bridge.request(
        BridgeChannels.REVIEW,
        BridgeActions.REQUEST_REVIEW,
        {},
        5000
      );
    } catch (e) {
      console.error('[NativeReview] requestReview error:', e);
    }
  }

  async openStore(): Promise<void> {
    try {
      await bridge.request(
        BridgeChannels.REVIEW,
        BridgeActions.OPEN_STORE,
        {},
        5000
      );
    } catch (e) {
      console.error('[NativeReview] openStore error:', e);
    }
  }

  isAvailable(): boolean {
    return true;
  }
}

const webReview = new WebReview();
const nativeReview = new NativeReview();

export function getReviewPlatform(): ReviewPlatform {
  return bridge.isNative ? nativeReview : webReview;
}
